var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { getSearchProvider } from './providers';
import { getDisplayPriceValue, exTaxFromDisplayDollars } from '../platforms/utils/priceUtils';
const isPriceRangeFilter = (id) => id.toLowerCase().includes('price');
// Namespace for filter keys in the URL so they don't clash with theme / other query params.
const URL_FILTER_PARAM_PREFIX = 'mrf_';
export function useSearchProvider(providerType, mockPlatform) {
    // Core search state
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [filters, setFilters] = useState([]);
    const [meta, setMeta] = useState(null);
    const [sortOptions, setSortOptions] = useState([]);
    const [provider] = useState(() => getSearchProvider(providerType, mockPlatform));
    // Preserve initial min/max for range filters so the slider bounds don't shrink
    const initialRangeBounds = useRef({});
    // Search parameters state
    const [searchState, setSearchState] = useState({
        query: '',
        currentSort: 'relevance',
        appliedFilters: {}
    });
    // URL parameter management — merge with existing query; only replace q, sort, and mrf_* keys.
    const updateUrlParams = useCallback(({ query, currentSort, appliedFilters }) => {
        // Start with the full list of URL params and delete any that we use (q, sort, and the prefix keys).
        // This is to ensure that we keep any parameters that aren't coming from us.
        // Otherwise, we would delete them on page load.
        const urlParams = new URLSearchParams(window.location.search);
        // Build a list of keys to delete, then delete them. We do this because if we tried to delete 
        // the keys during the forEach it would cause the loop skip later entries.
        const keysToDelete = new Set();
        urlParams.forEach((_, key) => {
            if (key === 'q' || key === 'sort' || key.startsWith(URL_FILTER_PARAM_PREFIX)) {
                keysToDelete.add(key);
            }
        });
        keysToDelete.forEach(key => urlParams.delete(key));
        if (currentSort && currentSort !== 'relevance') {
            urlParams.set('sort', currentSort);
        }
        if (query) {
            urlParams.set('q', query);
        }
        Object.entries(appliedFilters).forEach(([key, values]) => {
            const paramKey = `${URL_FILTER_PARAM_PREFIX}${key}`;
            values.forEach(value => {
                urlParams.append(paramKey, value);
            });
        });
        const qs = urlParams.toString();
        window.history.replaceState({}, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
    }, []);
    // Initialize from URL parameters
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const queryParam = urlParams.get('q');
        const sortParam = urlParams.get('sort');
        const initialFilters = {};
        urlParams.forEach((value, key) => {
            if (key !== 'q' && key !== 'sort' && key.startsWith(URL_FILTER_PARAM_PREFIX)) {
                const filterKey = key.replace(URL_FILTER_PARAM_PREFIX, '');
                if (!initialFilters[filterKey]) {
                    initialFilters[filterKey] = [];
                }
                initialFilters[filterKey].push(value);
            }
        });
        const newSearchState = {
            query: queryParam || '',
            currentSort: sortParam || 'relevance',
            appliedFilters: initialFilters
        };
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(newSearchState.query, {
            sort: newSearchState.currentSort || undefined,
            filters: newSearchState.appliedFilters
        });
    }, []);
    // Convert price filter values from incl-tax (display/state) to ex-tax (provider query)
    const toProviderFilters = (filters) => {
        const result = {};
        for (const [id, values] of Object.entries(filters)) {
            if (isPriceRangeFilter(id)) {
                result[id] = values.map(v => {
                    const parts = v.split(',').map(Number);
                    if (parts.length === 2) {
                        const exMin = Math.floor(exTaxFromDisplayDollars(parts[0]));
                        const exMax = Math.ceil(exTaxFromDisplayDollars(parts[1]));
                        return `${exMin},${exMax}`;
                    }
                    return v;
                });
            }
            else {
                result[id] = values;
            }
        }
        return result;
    };
    // Core search function
    const runSearch = (query_1, ...args_1) => __awaiter(this, [query_1, ...args_1], void 0, function* (query, options = {}) {
        setLoading(true);
        try {
            const providerOptions = Object.assign(Object.assign({}, options), { filters: options.filters ? toProviderFilters(options.filters) : options.filters });
            yield provider.search(query, providerOptions);
            setProducts(provider.getProducts());
            // Preserve initial min/max bounds for range filters; apply tax display to price ranges.
            const newFilters = provider.getFilters().map(filter => {
                if (filter.type === 'range' && filter.min != null && filter.max != null) {
                    const isPrice = isPriceRangeFilter(filter.id);
                    if (!initialRangeBounds.current[filter.id]) {
                        // First load — store bounds already converted for display
                        initialRangeBounds.current[filter.id] = {
                            min: isPrice ? Math.floor(getDisplayPriceValue(filter.min)) : filter.min,
                            max: isPrice ? Math.ceil(getDisplayPriceValue(filter.max)) : filter.max,
                        };
                    }
                    const bounds = initialRangeBounds.current[filter.id];
                    // currentMin/currentMax from provider are ex-tax applied values — convert for display
                    const currentMin = filter.currentMin != null
                        ? (isPrice ? Math.floor(getDisplayPriceValue(filter.currentMin)) : filter.currentMin)
                        : bounds.min;
                    const currentMax = filter.currentMax != null
                        ? (isPrice ? Math.ceil(getDisplayPriceValue(filter.currentMax)) : filter.currentMax)
                        : bounds.max;
                    return Object.assign(Object.assign({}, filter), { min: bounds.min, max: bounds.max, currentMin, currentMax });
                }
                return filter;
            });
            setFilters(newFilters);
            setMeta(provider.getMeta());
            setSortOptions(provider.getSortOptions());
        }
        catch (error) {
            console.error('Search failed:', error);
        }
        finally {
            setLoading(false);
        }
    });
    // Event handlers
    const handleQuerySubmit = (newQuery) => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { query: newQuery });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(newQuery, {
            sort: searchState.currentSort || undefined,
            filters: searchState.appliedFilters
        });
    };
    const handleFilterChange = (filterId, value) => {
        setSearchState(prev => {
            var _a;
            const newFilters = Object.assign({}, prev.appliedFilters);
            if (isPriceRangeFilter(filterId)) {
                // Store incl-tax value as-is — conversion to ex-tax happens in runSearch when querying the provider
                newFilters[filterId] = [value];
            }
            else {
                if ((_a = newFilters[filterId]) === null || _a === void 0 ? void 0 : _a.includes(value)) {
                    newFilters[filterId] = newFilters[filterId].filter(v => v !== value);
                    if (newFilters[filterId].length === 0) {
                        delete newFilters[filterId];
                    }
                }
                else {
                    newFilters[filterId] = [...(newFilters[filterId] || []), value];
                }
            }
            const newSearchState = Object.assign(Object.assign({}, prev), { appliedFilters: newFilters });
            updateUrlParams(newSearchState);
            runSearch(prev.query, {
                sort: prev.currentSort || undefined,
                filters: newFilters
            });
            return newSearchState;
        });
    };
    const handlePageChange = (page) => {
        runSearch(searchState.query, {
            page,
            sort: searchState.currentSort || undefined,
            filters: searchState.appliedFilters
        });
    };
    const handleRemoveFilter = (filterId, value) => {
        setSearchState(prev => {
            const newFilters = Object.assign({}, prev.appliedFilters);
            if (newFilters[filterId]) {
                newFilters[filterId] = newFilters[filterId].filter(v => v !== value);
                if (newFilters[filterId].length === 0)
                    delete newFilters[filterId];
            }
            const newSearchState = Object.assign(Object.assign({}, prev), { appliedFilters: newFilters });
            updateUrlParams(newSearchState);
            runSearch(prev.query, { sort: prev.currentSort || undefined, filters: newFilters });
            return newSearchState;
        });
    };
    const handleClearAllFilters = () => {
        setSearchState(prev => {
            const newSearchState = Object.assign(Object.assign({}, prev), { appliedFilters: {} });
            updateUrlParams(newSearchState);
            runSearch(prev.query, { sort: prev.currentSort || undefined, filters: {} });
            return newSearchState;
        });
    };
    const handleSortChange = (sort) => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { currentSort: sort });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(searchState.query, {
            sort: sort || undefined,
            filters: searchState.appliedFilters
        });
    };
    return {
        loading,
        products,
        filters,
        meta,
        sortOptions,
        query: searchState.query,
        currentSort: searchState.currentSort,
        appliedFilters: searchState.appliedFilters,
        handleQuerySubmit,
        handleFilterChange,
        handleRemoveFilter,
        handleClearAllFilters,
        handlePageChange,
        handleSortChange,
        runSearch
    };
}
